<template>
	<div>
		<div class="modal" :class="{ hide: !loading }">
			<div id="main-spinner" class="page-spinner-wrapper main-spinner">
				<span class="spinner-circle"></span>
			</div>
		</div>
		<div v-if="!loading" class="steam-result-wrapper">
			<div class="steam-result">
				<div class="steam-result-box">
					<div class="steam-result__status" :class="`steam-result__status--${resultStatus}`">
						{{ resultTitle }}
					</div>
					<div class="steam-result__description" :class="`steam-result__description--${resultStatus}`">
						<i18n :path="resultDescription" />
						<div v-if="resultStatus === 'failed' && errorCode" class="steam-result__errorcode">
							({{ $t('checkout.error-code', { errorCode: errorCode }) }})
						</div>
					</div>
					<div
						v-if="orderNumber || (product && (product.value || (product.currency && product.amount)))"
						class="steam-result__details-wrapper"
					>
						<div class="steam-result__details">
							<div
								v-if="product && (product.value || (product.currency && product.amount))"
								class="steam-result__details__row"
							>
								<span>{{ $t('checkout.steam.nx-credit-purchased') }}</span>
								<span>
									<template v-if="product.value">
										{{ $n(product.value) }}
									</template>
									<template v-if="product.amount && product.currency">
										({{ $n(product.amount, 'currency', product.currency) }})
									</template>
								</span>
							</div>
							<div v-if="orderNumber" class="steam-result__details__row">
								<span>{{ $t('checkout.order-number') }}</span>
								<span>{{ orderNumber }}</span>
							</div>
						</div>
					</div>
					<div v-if="resultStatus === 'failed'" class="steam-result__failed-support">
						<i18n path="checkout.questions-comments">
							<template v-slot:supportLink>
								<a target="_blank" :href="supportLink" @click="onSupportClick">{{
									$t('checkout.support-link-text')
								}}</a>
							</template>
						</i18n>
					</div>
					<div class="steam-result__action">
						<button class="btn btn-block btn-default" @click="close">
							{{ $t('checkout.steam.close-window') }}
						</button>
					</div>
				</div>

				<div class="steam-result__important">
					{{ $t('checkout.steam.important') }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import links from '@/config/links';

export default {
	name: 'SteamResult',
	metaInfo() {
		return {
			titleTemplate: `%s | ${this.$t('checkout.title')}`,
		};
	},
	data() {
		return {
			loading: false,
			errorCode: '',
			resultStatus: '',
			orderNumber: '',
			product: {},
		};
	},
	computed: {
		resultTitle() {
			switch (this.resultStatus) {
				case 'complete':
					return this.$t('checkout.purchase-complete-heading');
				case 'pending':
					return this.$t('checkout.steam.purchase-pending');
				default:
					// failed case
					if (this.errorCode === '002-400-0007') return this.$t('checkout.currently-under-review');
					else if (this.errorCode === '002-400-0008') return this.$t('checkout.still-under-review');
					return this.$t('checkout.purchase-failed');
			}
		},
		resultDescription() {
			switch (this.resultStatus) {
				case 'complete':
					return 'checkout.steam.success-description';
				case 'pending':
					return 'checkout.steam.pending-description';
				default:
					// failed case
					if (this.errorCode) {
						switch (this.errorCode) {
							// fraud errors
							case '002-400-0005':
								return 'checkout.steam.error.002-400-0005';
							case '002-400-0006':
								return 'checkout.steam.error.002-400-0006';
							case '002-400-0007':
							case '002-400-0008':
								return 'checkout.under-review-details';
							case '002-400-0010':
								return 'checkout.steam.error.002-400-0010';
							default:
								// others errors
								return 'checkout.unable-to-complete-purchase';
						}
					}
					return 'checkout.steam.unable-complete-purchase';
			}
		},
		supportLink() {
			return links[this.region].supportLink;
		},
		gamecode() {
			return this.$store.state.gamecode;
		},
	},
	async created() {
		this.loading = true;
		if (this.$route.query.gamecode) {
			this.$store.commit('setGameCode', this.$route.query.gamecode);
		}

		if (this.$store.state.gamecode) {
			const divCommerceId = document.getElementById('commerce');
			if (divCommerceId) divCommerceId.classList.add(this.$store.state.gamecode);
		}

		if (this.$route.query.result) {
			try {
				const result = this.$route.query.result; // it might be success or failed
				const checkoutId = this.$route.query.checkoutId;
				if (result === 'success') {
					const response = await this.$checkoutAPI.getTransactionSuccessResult(checkoutId);
					const data = response.data;
					this.resultStatus = data.status; // can be complete/pending/failed
					if (data.orderNumber) this.orderNumber = data.orderNumber;
					if (
						data.productPurchaseInfo &&
						data.productPurchaseInfo.products &&
						data.productPurchaseInfo.products[0]
					) {
						this.product = data.productPurchaseInfo.products[0];
					}
					if (this.resultStatus !== 'failed') {
						// complete and pending cases
						dataLayer.push({
							event: 'SW_Add Purchase Complete',
							'SW_Add Balance Game': this.gamecode,
							'SW_NX Amount': this.product.value,
							'Transaction status': this.resultStatus,
						});
					} else {
						this.failedTrackingEvent();
					}
				} else if (result === 'failed') {
					const response = await this.$checkoutAPI.getTransactionTypeResult(checkoutId, result);
					this.resultStatus = result;
					const data = response.data;
					if (data.orderNumber) this.orderNumber = data.orderNumber;
					this.failedTrackingEvent();
				}
				this.loading = false;
			} catch (error) {
				if (error && error.response && error.response.data && error.response.data.code) {
					this.errorCode = error.response.data.code;
				}
				this.resultStatus = 'failed';
				this.loading = false;
				this.failedTrackingEvent();
			}
		} else {
			this.resultStatus = 'failed';
			if (this.$route.query.error_code) this.errorCode = this.$route.query.error_code;
			this.loading = false;
			this.failedTrackingEvent();
		}
	},
	methods: {
		close() {
			this.$a2s.click('error_close');
			dataLayer.push({
				event: 'SW_Add Balance Return',
				'SW_Add Balance Game': this.gamecode,
			});
			window.close();
		},
		failedTrackingEvent() {
			this.$a2s.view('error_imp', {
				name: 'error_code',
				value: this.errorCode,
				objectTag: 'purchase_error',
			});
			dataLayer.push({
				event: 'SW_Add Balance Error',
				'SW_Add Balance Game': this.gamecode,
				'SW_Add Balance Error': this.errorCode ? this.errorCode : 'unknown',
			});
		},
		faqClick() {
			dataLayer.push({
				event: 'SW_Add Balance FAQ',
				'SW_Add Balance Game': this.gamecode,
			});
		},
		onSupportClick() {
			this.$a2s.click('error_support');
		},
	},
};
</script>

<style lang="less" scoped>
.steam-result-wrapper {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	min-height: 360px;
}
.steam-result {
	text-align: center;
	max-width: 565px;
	width: 100%;
	margin-left: 20px;
	margin-right: 20px;
	margin-top: 90px;
	@media (max-width: 565px) {
		margin-left: 0;
		margin-right: 0;
	}
	.steam-result-box {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		flex: 0 0 100%;
		max-width: 100%;
		background-color: rgba(#000, 75%);
		border: 1px solid rgba(#fff, 25%);
		border-radius: 5px;
		padding-left: 20px;
		padding-right: 20px;
		@media (max-width: 565px) {
			border-left: none;
			border-right: none;
			border-radius: 0;
			padding-left: 15px;
			padding-right: 15px;
		}
	}
	&__status {
		display: flex;
		flex-wrap: wrap;
		flex-direction: column;
		flex: 0 0 100%;
		max-width: 100%;
		font-size: 18px;
		font-weight: bold;
		color: #fff;
		padding-top: 20px;
		padding-bottom: 14px;
		&:before {
			content: '';
			display: block;
			width: 51px;
			height: 50px;
			background-repeat: no-repeat;
			background-size: contain;
			background-position: center center;
			margin-bottom: 17px;
			margin-left: auto;
			margin-right: auto;
		}
		&--failed {
			&:before {
				width: 53px;
				background-image: url('~@/images/purchase-failed.png');
			}
		}
		&--complete {
			&:before {
				background-image: url('~@/images/purchase-complete.png');
			}
		}
		&--pending {
			&:before {
				background-image: url('~@/images/purchase-pending.png');
			}
		}
	}
	&__description {
		font-size: 12px;
		line-height: 17px;
		color: #fff;
		margin-bottom: 15px;
		&--complete {
			padding: 0 100px;
		}
		@media (max-width: 565px) {
			padding: 0px;
		}
	}
	&__details-wrapper {
		flex: 0 0 100%;
		max-width: 100%;
		border-top: 1px solid rgba(#fff, 25%);
		margin-top: -10px;
	}
	&__details {
		padding-top: 2px;
		padding-bottom: 7px;
		text-align: left;
		font-size: 12px;
		&__row {
			padding-top: 6px;
			> span {
				display: block;
				color: rgba(#fff, 50%);
				&:last-child {
					padding-top: 2px;
					color: #fff;
				}
			}
		}
		@media (min-width: 520px) {
			display: table;
			padding-bottom: 15px;
			text-align: left;
			margin-right: auto;
			&__row {
				display: table-row;
				padding-top: 0;
				> span {
					display: table-cell;
					padding-top: 6px;
					&:last-child {
						padding-top: 6px;
					}
					&:first-child {
						padding-right: 20px;
					}
				}
			}
		}
	}
	&__failed-support {
		border-top: 1px solid rgba(#fff, 25%);
		flex: 0 0 100%;
		max-width: 100%;
		font-size: 12px;
		color: rgba(#fff, 50%);
		padding-top: 7px;
		padding-bottom: 15px;
		margin-top: -8px;
		a {
			text-transform: capitalize;
		}
	}
	&__errorcode {
		text-transform: lowercase;
		&::first-letter {
			text-transform: uppercase;
		}
	}
	&__action {
		flex: 0 0 100%;
		max-width: 100%;
		button {
			flex: 0 0 auto;
			width: auto;
			max-width: none;
			height: 20px;
			margin: 0px auto 16px auto;
			text-transform: uppercase;
			background-color: #07f;
			color: #fff;
			font-size: 12px;
			font-weight: bold;
			padding: 4px 20px;
			border-radius: 5px;
			border: none;
			&:not(:active):not(.disabled):hover {
				background-color: #4ca1ff;
			}
			&:not(.disabled):active {
				background-color: #0052b3;
				color: rgba(#fff, 50%);
			}
		}
	}
	&__important {
		font-size: 12px;
		line-height: 16px;
		color: rgba(#fff, 50%);
		margin-top: 5px;
		margin-bottom: 2px;

		@media (max-width: 565px) {
			margin-left: 44px;
			margin-right: 44px;
		}
		@media (max-width: 400px) {
			margin-left: 24px;
			margin-right: 24px;
		}
	}
	a {
		color: #fff;
		text-decoration: underline;
		text-underline-position: under;
		transition: color 0.25s;
		&:hover {
			color: #0077ff;
			transition: color 0.25s;
		}
		&:active {
			color: #4ca1ff;
			transition: color 0.25s;
		}
	}
}
</style>
